/** @format */

import 'src/i18n';
import { createSlice } from '@reduxjs/toolkit';
import axios from 'src/utils/axios';
import { ENDPOINTS } from 'src/constants';
import { resolveSelectedLanguage, getContentLanguage } from 'src/utils/locale';
import { resolveExtranetApiLanguageCode, resolveExtranetSelectableLocale } from 'src/utils/extranetLanguages';
import type { ContentViewApiResponse, SearchApiResponse, TopicApi, TopicWritePayload, TopicsPageResponse } from 'src/types';

const initialState = {
  activeTopicBrand: null,
  selectedLanguage: resolveExtranetSelectableLocale(resolveSelectedLanguage()),
  selectedSortBy: null,
  topicTreeLoading: true,
  topicTree: null,
  topicTreeError: null,
  contentItemsLoading: false,
  contentItemsAppending: false,
  contentItems: null,
  contentItemsNextCursor: null,
  contentItemsTotal: 0,
  contentItemsError: null,
  contentDetailLoading: false,
  contentDetail: null,
  contentDetailError: null,
  publishedTopicsLoading: true,
  publishedTopics: null,
  publishedTopicsError: null,
  createTopicLoading: false,
  createTopicError: null,
  updateTopicLoading: false,
  updateTopicError: null,
  togglePublishTopicLoading: false,
  togglePublishTopicError: null,
  expandedTopicKeys: {},
  activeTopicKey: '',
  topicDetail: null
};

const slice = createSlice({
  name: 'topics',
  initialState,
  reducers: {
    setActiveTopicBrand(state, action) {
      state.activeTopicBrand = action.payload;
      state.expandedTopicKeys = {};
      state.activeTopicKey = '';
    },
    setSelectedLanguage(state, action) {
      state.selectedLanguage = resolveExtranetSelectableLocale(action.payload, state.selectedLanguage || 'en');
    },
    setSelectedSortBy(state, action) {
      state.selectedSortBy = action.payload;
    },
    setActiveTopicKey(state, action) {
      state.activeTopicKey = action.payload;
    },
    toggleExpandedTopicKey(state, action) {
      const key = action.payload;
      state.expandedTopicKeys[key] = !state.expandedTopicKeys[key];
    },
    setExpandedTopicKeys(state, action) {
      state.expandedTopicKeys = action.payload || {};
    },
    clearExpandedTopicKeys(state) {
      state.expandedTopicKeys = {};
    },
    clearContentItems(state) {
      state.contentItems = [];
      state.contentItemsNextCursor = null;
      state.contentItemsTotal = 0;
      state.contentItemsError = null;
    },
    getTopicTree(state) {
      state.topicTreeLoading = true;
      state.topicTreeError = null;
    },
    getTopicTreeSuccess(state, action) {
      state.topicTree = action.payload;
      state.topicTreeLoading = false;
    },
    getTopicTreeFailure(state, action) {
      state.topicTreeError = action.payload;
      state.topicTreeLoading = false;
    },
    getContentItems(state, action) {
      state.contentItemsLoading = true;
      state.contentItemsAppending = !!action?.payload?.append;
      state.contentItemsError = null;
    },
    getContentItemsSuccess(state, action) {
      const { items, nextCursor, total, append } = action.payload || {};
      const safeItems = Array.isArray(items) ? items : [];
      const currentItems = Array.isArray(state.contentItems) ? state.contentItems : [];
      state.contentItems = append ? [...currentItems, ...safeItems] : safeItems;
      state.contentItemsNextCursor = nextCursor ?? null;
      if (typeof total === 'number') {
        state.contentItemsTotal = total;
      }
      state.contentItemsLoading = false;
      state.contentItemsAppending = false;
    },
    getContentItemsFailure(state, action) {
      state.contentItemsError = action.payload;
      state.contentItemsLoading = false;
      state.contentItemsAppending = false;
    },
    getContentDetailStart(state) {
      state.contentDetailLoading = true;
      state.contentDetailError = null;
    },
    getContentDetailSuccess(state, action) {
      state.contentDetail = action.payload;
      state.contentDetailLoading = false;
    },
    getContentDetailFailure(state, action) {
      state.contentDetailError = action.payload;
      state.contentDetailLoading = false;
    },
    resetContentDetail(state) {
      state.contentDetail = null;
      state.contentDetailLoading = false;
      state.contentDetailError = null;
    },
    getPublishedTopicsStart(state) {
      state.publishedTopicsLoading = true;
      state.publishedTopicsError = null;
    },
    getPublishedTopicsSuccess(state, action) {
      state.publishedTopics = action.payload;
      state.publishedTopicsLoading = false;
    },
    getPublishedTopicsFailure(state, action) {
      state.publishedTopicsError = action.payload;
      state.publishedTopicsLoading = false;
    },
    createTopicStart(state) {
      state.createTopicLoading = true;
      state.createTopicError = null;
    },
    createTopicSuccess(state) {
      state.createTopicLoading = false;
    },
    createTopicFailure(state, action) {
      state.createTopicError = action.payload;
      state.createTopicLoading = false;
    },
    updateTopicStart(state) {
      state.updateTopicLoading = true;
      state.updateTopicError = null;
    },
    updateTopicSuccess(state) {
      state.updateTopicLoading = false;
    },
    updateTopicFailure(state, action) {
      state.updateTopicError = action.payload;
      state.updateTopicLoading = false;
    },
    clearTopicEditorErrors(state) {
      state.createTopicError = null;
      state.updateTopicError = null;
    },
    togglePublishTopicStart(state) {
      state.togglePublishTopicLoading = true;
      state.togglePublishTopicError = null;
    },
    togglePublishTopicSuccess(state) {
      state.togglePublishTopicLoading = false;
    },
    togglePublishTopicFailure(state, action) {
      state.togglePublishTopicError = action.payload;
      state.togglePublishTopicLoading = false;
    },
    setTopicDetail(state, action) {
      state.topicDetail = action.payload;
    }
  }
});

export const reducer = slice.reducer;

function parseTopicsPagePayload(data: TopicsPageResponse | TopicApi[] | null | undefined) {
  if (Array.isArray(data)) {
    return { topics: data, nextCursor: null as string | null };
  }
  const topics = Array.isArray(data?.topics) ? data.topics : [];
  const nextCursor = data?.nextCursor ?? null;
  return { topics, nextCursor };
}

function getTopicSortName(topic) {
  return String(topic?.name ?? '').trim();
}

function compareTopicsByName(a, b) {
  return getTopicSortName(a).localeCompare(getTopicSortName(b), undefined, {
    sensitivity: 'base',
    numeric: true
  });
}

/** Recursively sort a topic tree by name (siblings at every level). */
export function sortTopicTreeByName(topics) {
  if (!Array.isArray(topics)) return [];
  return topics
    .map(topic => {
      if (!topic || typeof topic !== 'object') return topic;
      if (!Array.isArray(topic.children) || topic.children.length === 0) return topic;
      return { ...topic, children: sortTopicTreeByName(topic.children) };
    })
    .sort(compareTopicsByName);
}

async function fetchAllTopicsPages({
  published,
  language
}: { published?: boolean; language?: string | null } = {}) {
  let mergedTopics: TopicApi[] = [];
  let nextCursor: string | null = null;

  do {
    const params = new URLSearchParams();
    if (published !== undefined) {
      params.set('published', String(published));
    }
    if (nextCursor) {
      params.set('cursor', nextCursor);
    }
    const qs = params.toString();
    const url = `${ENDPOINTS.GET_TOPICS_ENDPOINT}${qs ? `?${qs}` : ''}`;

    const response = await axios.get<TopicsPageResponse>(url, {
      ...(language ? { acceptLanguage: getContentLanguage(resolveExtranetApiLanguageCode(language) || language) } : {})
    });
    const { topics, nextCursor: cursor } = parseTopicsPagePayload(response?.data);
    mergedTopics = [...mergedTopics, ...topics];
    nextCursor = cursor || null;
  } while (nextCursor);

  return sortTopicTreeByName(mergedTopics);
}

function formatError(error, fallback = 'Request failed') {
  return typeof error === 'string' ? error : error?.message || error?.error || fallback;
}

export function normalizeContentDetail(payload) {
  const raw =
    payload?.newsPost && typeof payload.newsPost === 'object'
      ? payload.newsPost
      : payload?.contentPost && typeof payload.contentPost === 'object'
        ? payload.contentPost
        : payload;
  if (!raw || typeof raw !== 'object') return null;

  const contentParts = Array.isArray(raw.contentParts) ? [...raw.contentParts] : [];
  const contentTexts = contentParts
    .filter(part => part?.type === 'content' && typeof part?.text === 'string' && part.text.trim())
    .map(part => part.text.trim());
  const documentsFromParts = contentParts
    .filter(part => part?.type === 'documents' && Array.isArray(part?.documents))
    .flatMap(part => part.documents)
    .filter(Boolean);
  const normalizedText = typeof raw.text === 'string' && raw.text.trim() ? raw.text : contentTexts.join('');
  const normalizedDocuments =
    Array.isArray(raw.documents) && raw.documents.length > 0 ? raw.documents : documentsFromParts;

  return {
    ...raw,
    contentParts,
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    topics: sortTopicTreeByName(Array.isArray(raw.topics) ? raw.topics : []),
    departments: Array.isArray(raw.departments) ? raw.departments : [],
    roles: Array.isArray(raw.roles) ? raw.roles : [],
    documents: normalizedDocuments,
    text: normalizedText,
    locale: raw.locale || null,
    pictureUrl: raw.pictureUrl || null
  };
}

function parseSearchPayload(data) {
  if (Array.isArray(data)) {
    return { items: data, nextCursor: null, total: data.length };
  }
  return {
    items: Array.isArray(data?.searchResults) ? data.searchResults : [],
    nextCursor: data?.nextCursor ?? null,
    total: typeof data?.total === 'number' ? data.total : 0
  };
}

function normalizeContentItem(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  return {
    ...item,
    date: item.creationDate || item.date || '',
    description: item.teaser || item.description || '',
    tags: Array.isArray(item.tags) ? item.tags : []
  };
}

function resolveSearchOrder(selectedSortBy) {
  if (!selectedSortBy) {
    return 'latest';
  }

  const normalized = String(selectedSortBy).trim().toLowerCase();
  const supportedOrders = ['relevant', 'latest', 'title', 'created', 'language'];

  if (supportedOrders.includes(normalized)) {
    return normalized;
  }

  return 'latest';
}

export const getTopicTree = (language?: string | null) => async (dispatch, getState) => {
  const { getTopicTree, getTopicTreeSuccess, getTopicTreeFailure } = slice.actions;
  const resolvedLanguage = language ?? getState().topics.selectedLanguage;

  try {
    dispatch(getTopicTree());
    const topics = await fetchAllTopicsPages({ language: resolvedLanguage });
    dispatch(getTopicTreeSuccess(topics));
  } catch (error) {
    const message = formatError(error, 'Failed to load topics tree');
    dispatch(getTopicTreeFailure(message));
  }
};

export const setTopicTreeLoading = () => dispatch => {
  const { getTopicTree } = slice.actions;
  dispatch(getTopicTree());
};

export const setPublishedTopicsLoading = () => dispatch => {
  dispatch(slice.actions.getPublishedTopicsStart());
};

export const getContentItems =
  ({
    activeTopicBrand,
    selectedLanguage,
    selectedSortBy,
    topicIds = null,
    limit = 25,
    cursor = null,
    append = false
  }) =>
  async dispatch => {
    const { getContentItems, getContentItemsSuccess, getContentItemsFailure } = slice.actions;

    try {
      dispatch(getContentItems({ append }));
      const order = resolveSearchOrder(selectedSortBy);
      const sort = order === 'title' ? 'ASC' : 'DESC';

      const searchParams = {
        ...(activeTopicBrand && activeTopicBrand !== 'all' ? { brand: activeTopicBrand } : {}),
        ...(selectedLanguage
          ? { 'languages[]': resolveExtranetApiLanguageCode(selectedLanguage) || selectedLanguage }
          : {}),
        ...(order ? { order } : {}),
        ...(Array.isArray(topicIds) && topicIds.length > 0 ? { 'topicIds[]': topicIds } : {}),
        ...(Number.isFinite(limit) ? { limit } : {}),
        ...(cursor ? { cursor } : {}),
        ...(sort ? { sort } : {})
      };

      const response = await axios.get<SearchApiResponse>(ENDPOINTS.SEARCH_ENDPOINT, {
        params: searchParams
      });
      const result = response?.data;
      const { items, nextCursor, total } = parseSearchPayload(result);
      let nextItems = items.map(normalizeContentItem).filter(Boolean);

      // if (activeTopicBrand && activeTopicBrand !== 'all') {
      //   nextItems = nextItems.filter(item => item.brand === activeTopicBrand);
      // }

      dispatch(
        getContentItemsSuccess({
          items: nextItems,
          nextCursor,
          total,
          append
        })
      );
    } catch (error) {
      dispatch(getContentItemsFailure(error?.message || 'Failed to load content items'));
    }
  };

export const getPublishedTopics = (language?: string | null) => async (dispatch, getState) => {
  const { getPublishedTopicsStart, getPublishedTopicsSuccess, getPublishedTopicsFailure } = slice.actions;
  const resolvedLanguage = language ?? getState().topics.selectedLanguage;

  try {
    dispatch(getPublishedTopicsStart());
    const mergedTopics = await fetchAllTopicsPages({ published: true, language: resolvedLanguage });
    dispatch(getPublishedTopicsSuccess(mergedTopics));
  } catch (error) {
    const message = formatError(error, 'Failed to load published topics');
    dispatch(getPublishedTopicsFailure(message));
  }
};

export const viewContentDetail = (contentId, language) => async dispatch => {
  const { getContentDetailStart, getContentDetailSuccess, getContentDetailFailure } = slice.actions;

  if (contentId === undefined || contentId === null || contentId === '') {
    const message = 'Content id is required';
    dispatch(getContentDetailFailure(message));
    return { success: false, error: message };
  }

  try {
    dispatch(getContentDetailStart());
    const apiLanguage = language ? getContentLanguage(resolveExtranetApiLanguageCode(language) || language) : null;
    const response = await axios.get<ContentViewApiResponse>(`${ENDPOINTS.GET_CONTENT_POST_ENDPOINT}/${contentId}/view`, {
      ...(apiLanguage ? { acceptLanguage: apiLanguage } : {})
    });
    const detail = normalizeContentDetail(response?.data);
    if (!detail) {
      const message = 'Content detail is empty';
      dispatch(getContentDetailFailure(message));
      return { success: false, error: message };
    }
    dispatch(getContentDetailSuccess(detail));
    return { success: true, detail };
  } catch (error) {
    const message = formatError(error, 'Failed to load content detail');
    dispatch(getContentDetailFailure(message));
    return { success: false, error: message };
  }
};

export const togglePublishTopic = topic => async dispatch => {
  const { togglePublishTopicStart, togglePublishTopicSuccess, togglePublishTopicFailure } = slice.actions;

  const topicId = topic?.id;
  if (topicId === undefined || topicId === null || topicId === '') {
    throw new Error('Topic id is required to toggle publish status');
  }

  const payload = {
    name: topic?.name ?? '',
    displayName: topic?.displayName ?? '',
    description: topic?.description ?? '',
    brand: topic?.brand ?? '',
    roles: Array.isArray(topic?.roles) ? topic.roles : [],
    parents: Array.isArray(topic?.parents) && topic.parents[0] ? topic.parents : [],
    published: !topic?.published
  };

  try {
    dispatch(togglePublishTopicStart());
    await axios.put(`${ENDPOINTS.GET_TOPICS_ENDPOINT}/${topicId}`, payload);
    dispatch(togglePublishTopicSuccess());
  } catch (error) {
    const message = formatError(error, 'Failed to toggle topic publish status');
    dispatch(togglePublishTopicFailure(message));
    throw error;
  }
};

export const createTopic = (payload: { topic: TopicWritePayload[] }, language?: string | null) => async dispatch => {
  const { createTopicStart, createTopicSuccess, createTopicFailure } = slice.actions;

  try {
    dispatch(createTopicStart());
    const response = await axios.post(ENDPOINTS.GET_TOPICS_ENDPOINT, payload);
    dispatch(createTopicSuccess());
    return response?.data;
  } catch (error) {
    const message = formatError(error, 'Failed to create topic');
    dispatch(createTopicFailure(message));
    throw error;
  }
};

export const updateTopic = (topicId: string | number, payload: { topic: TopicWritePayload[] }, language?: string | null) => async dispatch => {
  const { updateTopicStart, updateTopicSuccess, updateTopicFailure } = slice.actions;

  try {
    dispatch(updateTopicStart());
    const response = await axios.put(`${ENDPOINTS.GET_TOPICS_ENDPOINT}/${topicId}`, payload);
    dispatch(updateTopicSuccess());
    return response?.data;
  } catch (error) {
    const message = formatError(error, 'Failed to update topic');
    dispatch(updateTopicFailure(message));
    throw error;
  }
};

function buildUserTopicFavoritePath(uuid, topicId) {
  return `api/v1/users/${encodeURIComponent(String(uuid))}/topics/${encodeURIComponent(String(topicId))}/favorite`;
}

export const addUserTopicFavorite =
  ({ uuid, topicId }) =>
  async dispatch => {
    if (!uuid || topicId === undefined || topicId === null || String(topicId).trim() === '') {
      throw new Error('User id and topic id are required');
    }
    try {
      await axios.post(buildUserTopicFavoritePath(uuid, topicId));
      await dispatch(getPublishedTopics());
    } catch (error) {
      throw new Error(formatError(error, 'Failed to add topic to favourites'));
    }
  };

export const removeUserTopicFavorite =
  ({ uuid, topicId }) =>
  async (dispatch, getState) => {
    if (!uuid || topicId === undefined || topicId === null || String(topicId).trim() === '') {
      throw new Error('User id and topic id are required');
    }
    try {
      await axios.delete(buildUserTopicFavoritePath(uuid, topicId));
      await dispatch(getPublishedTopics());
      dispatch(slice.actions.setActiveTopicBrand('all'));
      const { selectedLanguage, selectedSortBy } = getState().topics;
      await dispatch(
        getContentItems({
          activeTopicBrand: 'all',
          selectedLanguage,
          selectedSortBy,
          topicIds: null
        })
      );
    } catch (error) {
      throw new Error(formatError(error, 'Failed to remove topic from favourites'));
    }
  };

const normalizeTopicDetail = data => {
  if (!data || typeof data !== 'object') return null;
  const topic = data?.topics?.[0];
  if (!topic || typeof topic !== 'object') return null;
  if (!Array.isArray(topic.children)) return topic;
  return {
    ...topic,
    children: topic.children.filter(child => child?.published !== false)
  };
};

export const getActiveTopicDetail =
  ({ topicId, language }) =>
  async dispatch => {
    try {
      const apiLanguage = language ? getContentLanguage(resolveExtranetApiLanguageCode(language) || language) : null;
      const response = await axios.get<TopicsPageResponse>(`${ENDPOINTS.GET_TOPICS_ENDPOINT}/${topicId}`, {
        ...(apiLanguage ? { acceptLanguage: apiLanguage } : {})
      });
      const detail = normalizeTopicDetail(response?.data);
      dispatch(setTopicDetail(detail));
      if (!detail) {
        return { success: false, status: null };
      }
      return { success: true, detail };
    } catch (error) {
      dispatch(setTopicDetail(null));
      const status = typeof error?.status === 'number' ? error.status : null;
      if (status === 404) {
        return { success: false, status: 404 };
      }
      throw new Error(formatError(error, 'Failed to load topic detail'));
    }
  };

export const {
  setActiveTopicBrand,
  setSelectedLanguage,
  setSelectedSortBy,
  setActiveTopicKey,
  toggleExpandedTopicKey,
  setExpandedTopicKeys,
  clearExpandedTopicKeys,
  clearContentItems,
  clearTopicEditorErrors,
  resetContentDetail,
  setTopicDetail
} = slice.actions;

export default slice;
